import {Auction, AuctionStatus, Leading, Leasing, Subjects, Tireset, Vehicle} from "domain-models"
import _ from 'lodash'
import moment from 'moment'
import React from "react"
import {setAuctions, showAuction, SubjectType} from 'redux/actions'
import {store} from "store"
import ImagePlaceholder from "../../components/ImagePlaceholder"
import Countdown from "../../components/Countdown"
import {hasFlag, make, model, monthYear, number, vehicleAttribute} from "../../filters"
import AuctionPrice from "../../components/Auction/AuctionPrice"
import WatchButton from "./AuctionDetails/WatchButton"
import {AuctionsState} from "../../redux/reducers/AuctionReducer"
import {connect} from "react-redux"
import {GlobalState} from "../../state"
import Trans from '../../components/Trans'

type AuctionListEntryProps = {
  auction: Auction
  auctions: AuctionsState
  subjectType: SubjectType
}

type AuctionListEntryState = {
  showDetails: boolean
  subject?: Subjects
  tireset?: Tireset
  vehicle?: Vehicle
  leading?: Leading
  leasing?: Leasing
  status: AuctionStatus
}

class AuctionListEntry extends React.Component<AuctionListEntryProps, AuctionListEntryState> {
  constructor(props: AuctionListEntryProps) {
    super(props)

    let subject

    const vehicle = _.first(props.auction.subjects)?.vehicle
    const tireset = _.first(props.auction.subjects)?.tireset
    const leading = _.first(props.auction.subjects)?.leading
    const leasing = _.first(props.auction.subjects)?.leasing

    subject = vehicle || tireset || leading || leasing

    this.state = {
      status: props.auction.status || 'ongoing',
      showDetails: false,
      subject,
      vehicle,
      tireset,
      leading,
      leasing,
    }
  }

  componentDidMount() {
    this.setCountdown()
  }

  componentWillUnmount() {
    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  showDetails = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (this.props.auction.status !== 'ended' || _.get(store.getState(), 'auctions.filter.type', false) === 'auctioned') {
      store.dispatch(showAuction(this.props.auction))
    }
  }

  render() {
    if (!this.state.subject) {
      return <tr>
        <td>No subject</td>
      </tr>
    }

    const auction = this.props.auction
    const firstImage = _.first(this.state?.subject?.images || [])
    const dealer = this.state.subject.dealer
    const disabled = (this.state.status === 'ended' || this.state.status === 'not-started-yet') && _.get(store.getState(), 'auctions.filter.type', false) !== 'auctioned'
    return (
      <tr className={"w-full hover:bg-gray-200 cursor-pointer" + (disabled ? ' opacity-50 ' : '')} onClick={this.showDetails}>
        {
          firstImage ?
            <td className="bg-cover bg-center h-16 border-t border-b px-2 py-1 min-w-16" style={{backgroundImage: "url(" + firstImage + ")"}} onClick={this.showDetails}/>
            :
            <td className={'h-16 w-16 border-t border-b px-2 py-1 min-w-16'}><ImagePlaceholder small={true}/></td>
        }

        <td className={'truncate px-2 py-1 border'}>
          <Countdown auction={auction} timeTillDate={auction.round.EndsOn} hotBid={this.props.auction.status === 'hot-bid'} small={true} onlyLargest={true}/>
        </td>

        <td className={'truncate px-2 py-1 border'}>{make(this.state.subject.Make)}</td>
        <td className={'truncate px-2 py-1 border'}>{model(this.state.subject.Make, this.state.subject.Model)}</td>
        <td className={'truncate px-2 py-1 border w-32 md:w-64'}>
          <p>
            <span className={'px-1 hidden md:block'}>{dealer.FirmExtra}</span>
            <span className={'px-1 hidden md:block'}> {dealer.Street}</span>
            <span className={'px-1 md:block'}> {dealer.PostalCode}</span>
            <span className={'px-1 md:block'}> {dealer.City}</span>
          </p>
        </td>
        <td className={'px-2 py-1 border'}><AuctionPrice auction={auction} small={true}/></td>

        {
          this.props.subjectType === 'Vehicle' && this.state.vehicle ?
            <>
              <td className={'p-2 font-semibold'}>{number(this.state.vehicle.Kilometers, 0, 'km')}</td>
              <td className={'p-2 font-semibold'}>{monthYear(this.state.vehicle.RegistrationDate)}</td>
              <td className={'p-2 font-semibold'}>{number(this.state.vehicle.Power, 0, 'kw')}</td>
              <td className={'p-2 font-semibold'}><Trans>{vehicleAttribute('Transmission', this.state.vehicle.Transmission)}</Trans></td>
              <td className={'p-2 font-semibold'}><Trans>{vehicleAttribute('Fuel', this.state.vehicle.Fuel)}</Trans></td>
              <td className={'p-2 font-semibold'}>
                <span className='w-full'>
                  {
                    hasFlag(this.state.vehicle, 'SellFlags', 32768) ?
                      <span><Trans>MwST. ausweisbar</Trans></span>
                      :
                      <span><Trans>MwST. nicht ausweisbar</Trans></span>
                  }
                </span>
              </td>
            </>
            : null
        }
        {
          this.props.subjectType === 'Vehicle' && (this.state.leading || this.state.leasing) ?
            <>
              <td className={'p-2 font-semibold'}>-</td>
              <td className={'p-2 font-semibold'}>-</td>
              <td className={'p-2 font-semibold'}>-</td>
              <td className={'p-2 font-semibold'}>-</td>
              <td className={'p-2 font-semibold'}>-</td>
              <td className={'p-2 font-semibold'}>
                <span className='w-full'>-</span>
              </td>
            </>
            : null
        }
        <td className={'border-t border-b cursor-default px-2 py-1'}>
          <span className={'flex justify-end items-center'}>
            <WatchButton auction={auction}/>
          </span>
        </td>
      </tr>
    )
  }

  private setCountdown = () => {
    const timeTillDate = this.props.auction.round.EndsOn
    const now = moment()
    const countdown = moment(timeTillDate)

    const days = countdown.diff(now, 'days')
    const hours = countdown.subtract(days, 'days').diff(now, 'hours')
    const minutes = countdown.subtract(hours, 'hours').diff(now, 'minutes')
    const seconds = countdown.subtract(minutes, 'minutes').diff(now, 'seconds')

    let status = 'ongoing' as AuctionStatus
    if (!days && !hours && minutes < 5) {
      status = 'hot-bid'
    }

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      status = 'ended'
    }

    if (this.props.auction.round.StartsOn && moment(this.props.auction.round.StartsOn).isAfter(now)) {
      status = 'not-started-yet'
    }

    if (this.state.status !== status) {
      this.setState({status})
      this.props.auction.status = status
      store.dispatch(setAuctions(this.props.auctions.auctions))
    }
  }

  private interval = setInterval(this.setCountdown, 1000)
}

export default connect((state: GlobalState) => ({
  auctions: state.auctions,
  subjectType: state.app.subjectType
}))(AuctionListEntry)
